import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import type { OrgProjectionDiagramSetResult } from '../models/OrgProjectionDiagramSet';

const inFlightEnsures = new Map<string, Promise<OrgProjectionDiagramSetResult>>();

export class OrgProjectionDiagramsEnsureError extends Error {
  constructor(readonly status: number) {
    super(`Failed to create Atlas charts (${status})`);
    this.name = 'OrgProjectionDiagramsEnsureError';
  }
}

export async function ensureOrgProjectionDiagrams(
  fetcher: (input: string, init?: RequestInit) => Promise<Response>,
  folderId: string,
): Promise<OrgProjectionDiagramSetResult> {
  const existing = inFlightEnsures.get(folderId);
  if (existing) return existing;

  const request = (async () => {
    const response = await fetcher(
      `/api/folders/${encodeURIComponent(folderId)}/atlas/projection-diagrams`,
      { method: 'POST' },
    );
    if (!response.ok) throw new OrgProjectionDiagramsEnsureError(response.status);
    return response.json() as Promise<OrgProjectionDiagramSetResult>;
  })();
  inFlightEnsures.set(folderId, request);
  try {
    return await request;
  } finally {
    if (inFlightEnsures.get(folderId) === request) {
      inFlightEnsures.delete(folderId);
    }
  }
}

/** Ensures the default canonical Atlas projection diagrams after folder import. */
export function useEnsureOrgProjectionDiagrams(folderId: string) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => ensureOrgProjectionDiagrams(authenticatedFetch, folderId),
    onSuccess: () => {
      // This POST creates the Full Org Chart, the Position Chart and the
      // Department Views child folder. The folder panel does not read any of
      // those from ['dashboard'] — it lists diagrams from
      // ['folder', folderId, 'children'] (30s staleTime) and reads the folder
      // itself from ['folder', folderId]. Invalidating only ['dashboard'] left
      // both holding their pre-import values, so after a CSV import the panel
      // showed no charts until the user manually refreshed the browser.
      // Artifact creation changes diagram/folder listings, not the canonical
      // Atlas model. Avoid invalidating lenses and Compass graph projections.
      // The artifacts are ready when the POST succeeds. Active dashboard
      // refetches can be expensive after a large import, so do not keep the
      // mutation (and its disabled chart button) pending while they settle.
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['folder', folderId] });
    },
  });
  return { ensure: mutation.mutateAsync, isPending: mutation.isPending };
}
